import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { tap } from 'rxjs/operators';
import { DimensionsUnitsEnum } from 'src/app/core/enums/dimensions-units.enum';
import convertDimensions from 'src/app/core/helpers/dimensions-weight';
import { packageValidator } from 'src/app/core/validators/package-validator';
import { Product } from 'src/app/PIM/models/product.model';
import { WeightUnitsEnum } from '../../../../core/enums/weight-units.enum';
import convertWeight from '../../../../core/helpers/convert-weight';
import { WarehouseListInterface } from '../../../../core/models/warehouse-list.interface';
import { ManufacturerService } from '../../../../core/services/manufacturer.service';
import { WarehouseService } from '../../../../core/services/warehouse.service';

@Component({
  selector: 'app-product-add-new',
  templateUrl: './product-add-new.component.html',
})
export class ProductAddNewComponent implements OnInit {
  @Output() onSave = new EventEmitter();

  @Output() onCancel = new EventEmitter();

  warehouses: WarehouseListInterface[] = [];

  submitted = false;

  form = this.fb.group(
    {
      name: [null, Validators.required],
      sku: [null, Validators.required],
      ean: [null, Validators.required],
      width: [null as number, [Validators.min(0)]],
      height: [null as number, [Validators.min(0)]],
      length: [null as number, [Validators.min(0)]],
      weight: [null as number, [Validators.min(0)]],
      manufacturer: [null, Validators.required],
      warehouseIds: [null, Validators.required],
      packageWidth: [null as number | null, [Validators.min(0)]],
      packageHeight: [null as number | null, [Validators.min(0)]],
      packageLength: [null as number | null, [Validators.min(0)]],
      packageCapacity: [null as number | null, [Validators.min(0)]],
    },
    { validators: packageValidator },
  );

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialog,
    private manufacturerService: ManufacturerService,
    private warehouseService: WarehouseService,
  ) {}

  ngOnInit() {
    this.warehouseService
      .getWarehouseList()
      .pipe(tap(({ items }) => (this.warehouses = items)))
      .subscribe();

    this.form.valueChanges.subscribe(() => {
      this.updatePackageValidators();
    });
  }

  private updatePackageValidators() {
    const packageFields = ['packageWidth', 'packageHeight', 'packageLength', 'packageCapacity'];
    const hasAnyPackageValue = packageFields.some(field => this.form.get(field)?.value);

    packageFields.forEach(fieldName => {
      const field = this.form.get(fieldName);
      if (hasAnyPackageValue) {
        field?.setValidators([Validators.min(0), Validators.required]);
      } else {
        field?.setValidators([Validators.min(0)]);
      }
      field?.updateValueAndValidity({ emitEvent: false });
    });
  }

  manufacturerList$ = (query: string) => this.manufacturerService.getManufacturerAutocompleteList(query);

  save(): void {
    this.submitted = true;

    if (this.form.valid) {
      const payload: Partial<Product> = {
        ...this.form.value,
        weight: convertWeight(this.form.value.weight, WeightUnitsEnum.KG, WeightUnitsEnum.G),
        dimensions: {
          width: this.form.value.width > 0 ? convertDimensions(this.form.value.width, DimensionsUnitsEnum.CM, DimensionsUnitsEnum.MM) : null,
          height: this.form.value.height > 0 ? convertDimensions(this.form.value.height, DimensionsUnitsEnum.CM, DimensionsUnitsEnum.MM) : null,
          length: this.form.value.length > 0 ? convertDimensions(this.form.value.length, DimensionsUnitsEnum.CM, DimensionsUnitsEnum.MM) : null,
        },
      };

      const hasPackageData =
        this.form.value.packageCapacity ||
        this.form.value.packageWidth ||
        this.form.value.packageHeight ||
        this.form.value.packageLength;

      if (hasPackageData) {
        payload.package = {
          capacity: this.form.value.packageCapacity,
          dimensions: {
            width: this.form.value.packageWidth > 0 ? convertDimensions(this.form.value.packageWidth, DimensionsUnitsEnum.CM, DimensionsUnitsEnum.MM) : null,
            height: this.form.value.packageHeight > 0 ? convertDimensions(this.form.value.packageHeight, DimensionsUnitsEnum.CM, DimensionsUnitsEnum.MM) : null,
            length: this.form.value.packageLength > 0 ? convertDimensions(this.form.value.packageLength, DimensionsUnitsEnum.CM, DimensionsUnitsEnum.MM) : null,
          },
        };
      }
      // @ts-ignore
      delete payload.width;
      // @ts-ignore
      delete payload.height;
      // @ts-ignore
      delete payload.length;
      // @ts-ignore
      delete payload.packageWidth;
      // @ts-ignore
      delete payload.packageHeight;
      // @ts-ignore
      delete payload.packageLength;
      // @ts-ignore
      delete payload.packageCapacity;

      this.onSave.emit(payload);
      this.dialogRef.closeAll();
    } else {
      this.form.markAllAsTouched();
    }
  }

  cancel(): void {
    this.onCancel.emit();
    this.dialogRef.closeAll();
  }

  get manufacturerControl(): FormControl {
    return this.form.get('manufacturer') as FormControl;
  }

  updateManufacturer(event) {
    this.form.patchValue({
      manufacturer: event?.option?.value,
    });
  }
}
